import { useState, useCallback, useMemo } from 'react';
import PlantillaRow from './PlantillaRow';
import MovePlantillaModal from '../MovePlantillaModal';
import { postAPI } from '../../../utils/utils';
import './EditorTable.css';

function SortableHeader({ field, children, sortBy, sortDirection, onSort, style }) {
    const isActive = sortBy === field;
    const arrow = !isActive ? '' : sortDirection === 'asc' ? ' \u2191' : ' \u2193';

    return (
        <th
            className={`sortable-header${isActive ? ' active' : ''}`}
            onClick={() => onSort(field)}
            style={style}
        >
            {children}{arrow}
        </th>
    );
}

function PlantillesTable({
    plantilles,
    bundles,
    searchTerm,
    socket,
    folders,
    folderItems,
    currentFolderId,
    onFoldersChange,
    onDeleteFolder,
    loading,
    userId,
}) {
    const [sortBy, setSortBy] = useState('nom');
    const [sortDirection, setSortDirection] = useState('asc');
    const [movingPlantilla, setMovingPlantilla] = useState(null);

    const handleSort = useCallback((field) => {
        if (sortBy === field) {
            setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(field);
            setSortDirection('asc');
        }
    }, [sortBy]);

    // Build lookup: folderId -> folderName
    const folderNameMap = useMemo(() => {
        const map = {};
        (folders || []).forEach(f => { map[f.id] = f.nom; });
        return map;
    }, [folders]);

    // Build lookup: plantilla -> bundles using it
    const bundlesMap = useMemo(() => {
        const map = {};
        (bundles || [])
            .filter(b => !b.hidden && b.parts)
            .forEach(bundle => {
                Object.entries(bundle.parts).forEach(([part, plnt]) => {
                    if (!map[plnt]) map[plnt] = [];
                    map[plnt].push({ ...bundle, part });
                });
            });
        return map;
    }, [bundles]);

    // Filter by folder
    const plantillesInFolder = useMemo(() => {
        if (!currentFolderId) return plantilles;
        return plantilles.filter(p => folderItems[p] === currentFolderId);
    }, [plantilles, currentFolderId, folderItems]);

    // Filter by search
    const filteredPlantilles = useMemo(() => {
        if (!searchTerm) return plantillesInFolder;
        const term = searchTerm.toLowerCase();
        return plantillesInFolder.filter(p => {
            if (p.toLowerCase().includes(term)) return true;
            const usedBy = bundlesMap[p] || [];
            return usedBy.some(b =>
                b.nom?.toLowerCase()?.includes(term) ||
                b.shortName?.toLowerCase()?.includes(term)
            );
        });
    }, [plantillesInFolder, searchTerm, bundlesMap]);

    // Sort
    const sortedPlantilles = useMemo(() => {
        return [...filteredPlantilles].sort((a, b) => {
            const dir = sortDirection === 'asc' ? 1 : -1;

            if (sortBy === 'nom') {
                return dir * a.localeCompare(b);
            }
            return 0;
        });
    }, [filteredPlantilles, sortBy, sortDirection]);

    const handleMoveToFolder = useCallback((plantillaName) => {
        setMovingPlantilla(plantillaName);
    }, []);

    const handleMoveConfirm = useCallback((folderId) => {
        if (!movingPlantilla) return;
        postAPI('/plantilles-folders/move', {
            plantillaName: movingPlantilla,
            folderId: folderId || null,
        }, () => {
            onFoldersChange();
            setMovingPlantilla(null);
        });
    }, [movingPlantilla, onFoldersChange]);

    const folderIsEmpty = currentFolderId && plantillesInFolder.length === 0;

    if (loading) return (
        <div className="editor-table-container">
            <table className="editor-table editor-table-compact">
                <thead>
                    <tr>
                        <th style={{ width: 44 }}></th>
                        <th style={{ width: '1%', whiteSpace: 'nowrap' }}>Nom</th>
                        <th style={{ maxWidth: 600 }}>Usada a</th>
                        <th style={{ whiteSpace: 'nowrap' }}>Dins carpeta</th>
                        <th style={{ width: 44 }}></th>
                    </tr>
                </thead>
                <tbody>
                    {Array.from({ length: 5 }, (_, i) => (
                        <tr key={i} className="skeleton-row">
                            <td><div className="skeleton-bar" style={{ width: 16, height: 16, margin: '0 auto' }} /></td>
                            <td><div className="skeleton-bar" style={{ width: '80%' }} /></td>
                            <td><div className="skeleton-bar" style={{ width: '60%' }} /></td>
                            <td><div className="skeleton-bar" style={{ width: '50%' }} /></td>
                            <td></td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );

    if (sortedPlantilles.length === 0) {
        return (
            <div className="editor-empty-state">
                <div className="editor-empty-state-icon">&#128269;</div>
                <p className="editor-empty-state-text">
                    {searchTerm ? 'Cap plantilla trobada' : 'No hi ha plantilles'}
                </p>
                {folderIsEmpty ? (
                    <button
                        className="editor-folder-delete-btn"
                        onClick={() => {
                            const folderName = folderNameMap[currentFolderId] || '';
                            const confirmed = window.confirm(`Eliminar la carpeta "${folderName}"?`);
                            if (confirmed) onDeleteFolder(currentFolderId);
                        }}
                    >
                        Eliminar carpeta buida
                    </button>
                ) : null}
            </div>
        );
    }

    return (
        <>
            <div className="editor-table-container">
                <table className="editor-table editor-table-compact">
                    <thead>
                        <tr>
                            <th style={{ width: 44 }}></th>
                            <SortableHeader field="nom" sortBy={sortBy} sortDirection={sortDirection} onSort={handleSort} style={{ width: '1%', whiteSpace: 'nowrap' }}>
                                Nom
                            </SortableHeader>
                            <th style={{ maxWidth: 600 }}>Usada a</th>
                            <th style={{ whiteSpace: 'nowrap' }}>Dins carpeta</th>
                            <th style={{ width: 44 }}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {sortedPlantilles.map(plantilla => (
                            <PlantillaRow
                                key={plantilla}
                                plantilla={plantilla}
                                bundlesUsing={bundlesMap[plantilla] || []}
                                socket={socket}
                                sortBy={sortBy}
                                folderName={folderItems[plantilla] ? folderNameMap[folderItems[plantilla]] : null}
                                onMoveToFolder={handleMoveToFolder}
                                userId={userId}
                            />
                        ))}
                    </tbody>
                </table>
            </div>

            {movingPlantilla ? (
                <MovePlantillaModal
                    plantillaName={movingPlantilla}
                    folders={folders}
                    currentFolderId={folderItems[movingPlantilla] || null}
                    onMove={handleMoveConfirm}
                    onClose={() => setMovingPlantilla(null)}
                />
            ) : null}
        </>
    );
}

export default PlantillesTable;
